import Vue from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { translate } from 'vue-gettext'
import downloadSessionStatus from './downloadSessionStatus'
import downloadStrategy from './downloadStrategy'
import Downloader from '@/domain/core/utils/downloader'
import taskObserver from '@/domain/core/utils/taskObserver'
import mediaDownloadService from '@/domain/mediaDownload/service'

const { gettext: $gettext, gettextInterpolate: $gettextInterpolate } = translate

// localStorage persistence for longtask sessions
const STORAGE_KEY = 'downloader_longtask_sessions'

function persistSession(session) {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    const index = stored.findIndex(s => s.sessionId === session.sessionId)
    if (index >= 0) stored[index] = session
    else stored.push(session)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
}

function unpersistSession(sessionId) {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored.filter(s => s.sessionId !== sessionId)))
}

function getPersistedSessions() {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
}

// Download configuration from environment
const BATCH_SIZE = parseInt(process.env.VUE_APP_DOWNLOAD_BATCH_SIZE) || 50
const EMAIL_LIMIT = parseInt(process.env.VUE_APP_DOWNLOAD_EMAIL_LIMIT) || 500
const EMAIL_DELIVERY_ENABLED = process.env.VUE_APP_DOWNLOAD_EMAIL_DELIVERY_ENABLED !== '0'

// Strategy thresholds (DIRECT_LIMIT correlates with BATCH_SIZE)
const DIRECT_LIMIT = BATCH_SIZE

// Retry configuration for 502 errors
const RETRY_DELAYS = [2000, 4000, 12000, 12000] // 2s, 4s, 12s

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function is502Error(error) {
    return error?.response?.status === 502 ||
           error?.status === 502 ||
           (typeof error?.message === 'string' && error.message.includes('502'))
}

/**
 * Wraps a function with retry logic for 502 errors
 * @param {Function} fn - Async function to execute
 * @returns {Promise} - Result of fn or throws after all retries exhausted
 */
async function withRetry(fn) {
    let lastError

    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        try {
            return await fn()
        } catch (error) {
            lastError = error

            if (!is502Error(error)) {
                throw error
            }

            if (attempt < RETRY_DELAYS.length) {
                await sleep(RETRY_DELAYS[attempt])
            }
        }
    }

    throw lastError
}

/**
 * Returns a user-friendly error message or null for generic errors.
 * Returning null allows the UI component to use translated fallback message.
 */
function getUserFriendlyError() {
    return null
}

/**
 * Extracts all available zip part URLs from any message format.
 * Normalizes MQTT events (progress/completed) and poll responses (running/completed)
 * into a flat array of URLs.
 */
function extractZipPartUrls(data, source) {
    const urls = []

    if (source === 'mqtt') {
        if (data.zip_part?.url) {
            urls.push(data.zip_part.url)
        }
        if (Array.isArray(data.zip_parts)) {
            for (const part of data.zip_parts) {
                const url = typeof part === 'string' ? part : part?.url
                if (url) urls.push(url)
            }
        }
    } else if (source === 'poll') {
        const zipParts = data.result?.zip_parts
        if (Array.isArray(zipParts)) {
            for (const part of zipParts) {
                const url = typeof part === 'string' ? part : part?.url
                if (url) urls.push(url)
            }
        }
    }

    return urls
}

/**
 * Downloads zip parts that haven't been downloaded yet.
 * Mutates downloadedParts in place, adding newly downloaded URLs.
 */
function downloadMissingParts(availableUrls, downloadedParts) {
    for (const url of availableUrls) {
        if (!downloadedParts.includes(url)) {
            downloadedParts.push(url)
            Downloader.downloadFromUrl(url)
        }
    }
}

/**
 * Determines whether a message represents a final state and extracts metadata.
 * Final states (completed/failed) don't need further observation.
 */
function resolveMessageState(data, source) {
    if (source === 'mqtt') {
        if (data.type === 'media_zip.completed') {
            return {
                isFinal: true,
                status: downloadSessionStatus.completed,
                filesProcessed: data.total_media_processed,
                failedDownloads: data.failed_downloads || 0
            }
        }
        if (data.type === 'media_zip.failed') {
            return {
                isFinal: true,
                status: downloadSessionStatus.failed,
                error: data.error || null
            }
        }
        return {
            isFinal: false,
            percentage: data.percentage || 0,
            filesProcessed: data.files_processed
        }
    }

    // source === 'poll'
    if (data.status === 'completed') {
        return {
            isFinal: true,
            status: downloadSessionStatus.completed,
            filesProcessed: data.result?.total_media_processed,
            failedDownloads: data.result?.failed_downloads || 0
        }
    }
    if (data.status === 'failed') {
        return {
            isFinal: true,
            status: downloadSessionStatus.failed,
            error: data.errors?.[0] || null
        }
    }
    // running
    return {
        isFinal: false,
        percentage: data.result?.percentage,
        filesProcessed: data.result?.files_processed
    }
}

const state = {
    sessions: [],
    batchSize: BATCH_SIZE,
    // Email modal state
    emailRequest: null,  // { name, count, blocked, resolve, reject }
    // Pre-flight "wait vs email" prompt state
    deliveryChoiceRequest: null,  // { name, count, resolve, reject }
}

export const getters = {
    sessions: state => state.sessions,
    sessionById: state => id => state.sessions.find(s => s.id === id),
    activeSessions: state => state.sessions.filter(s =>
        s.status === downloadSessionStatus.pending ||
        s.status === downloadSessionStatus.inProgress
    ),
    hasActiveDownload: (state, getters) => getters.activeSessions.length > 0,
    batchSize: state => state.batchSize,
    sessionSummary: state => {
        return state.sessions.reduce((acc, session) => {
            acc.totalFiles += session.totalFiles
            acc.downloadedFiles += session.downloadedFiles
            return acc
        }, { totalFiles: 0, downloadedFiles: 0 })
    },
    emailRequest: state => state.emailRequest,
    deliveryChoiceRequest: state => state.deliveryChoiceRequest,

    /**
     * Determines download strategy based on count, limits, and available callbacks.
     * Logic: from strictest requirement to fallback, email as last resort before blocking
     */
    getStrategy: (state, getters, rootState, rootGetters) => (count, callbacks = {}) => {
        const isLimited = rootGetters['features/isLimitedResourcesEnabled']
        const canDirect = typeof callbacks.onDirect === 'function'
        const canBatch = typeof callbacks.onBatch === 'function'
        const canEmail = typeof callbacks.onEmail === 'function' && !isLimited
        const canLongTask = typeof callbacks.onLongTask === 'function'

        // Above EMAIL_LIMIT: longtask preferred, email fallback
        if (count > EMAIL_LIMIT) {
            if (canLongTask) return downloadStrategy.longtask
            if (canEmail) return downloadStrategy.email
            return downloadStrategy.blocked
        }

        // Above DIRECT_LIMIT: longtask or batch preferred, email as fallback
        if (count > DIRECT_LIMIT) {
            if (canLongTask) return downloadStrategy.longtask
            if (canBatch) return downloadStrategy.batch
            if (canEmail) return downloadStrategy.email
            return downloadStrategy.blocked
        }

        // Within DIRECT_LIMIT: direct preferred, batch/longtask/email as fallback
        if (canDirect) return downloadStrategy.direct
        if (canBatch) return downloadStrategy.batch
        if (canLongTask) return downloadStrategy.longtask
        if (canEmail) return downloadStrategy.email
        return downloadStrategy.blocked
    },

    /**
     * Returns limits for UI display
     */
    limits: () => ({
        directLimit: DIRECT_LIMIT,
        emailLimit: EMAIL_LIMIT
    })
}

export const mutations = {
    createSession(state, session) {
        state.sessions.push(session)
    },
    updateSession(state, { id, ...updates }) {
        const index = state.sessions.findIndex(s => s.id === id)
        if (index === -1) return
        Vue.set(state.sessions, index, { ...state.sessions[index], ...updates })
    },
    removeSession(state, id) {
        state.sessions = state.sessions.filter(s => s.id !== id)
    },
    // Email modal mutations
    setEmailRequest(state, request) {
        state.emailRequest = request
    },
    resolveEmailRequest(state, email) {
        if (state.emailRequest?.resolve) {
            state.emailRequest.resolve(email)
        }
        state.emailRequest = null
    },
    rejectEmailRequest(state) {
        if (state.emailRequest?.reject) {
            state.emailRequest.reject(new Error('User cancelled'))
        }
        state.emailRequest = null
    },
    // Delivery-choice modal mutations
    setDeliveryChoiceRequest(state, request) {
        state.deliveryChoiceRequest = request
    },
    resolveDeliveryChoiceRequest(state, choice) {
        if (state.deliveryChoiceRequest?.resolve) {
            state.deliveryChoiceRequest.resolve(choice)
        }
        state.deliveryChoiceRequest = null
    },
    rejectDeliveryChoiceRequest(state) {
        if (state.deliveryChoiceRequest?.reject) {
            state.deliveryChoiceRequest.reject(new Error('User cancelled'))
        }
        state.deliveryChoiceRequest = null
    }
}

export const actions = {
    /**
     * Main download action. Decides strategy based on count and available callbacks.
     * Handles iteration for batch downloads internally.
     *
     * @param {string} name - Display name for UI
     * @param {number} count - Total file count
     * @param {Function} [onDirect] - Direct download: async () => void
     * @param {Function} [onBatch] - Batch download: async (page, limit) => downloadedCount
     * @param {Function} [onEmail] - Email download: async (email) => void
     */
    async download({ state, getters, commit, dispatch }, { name, count, onDirect, onBatch, onEmail, onLongTask }) {
        // Get strategy considering count, limits, and available callbacks
        const strategy = getters.getStrategy(count, { onDirect, onBatch, onEmail, onLongTask })

        // Execute based on final strategy
        switch (strategy) {
            case downloadStrategy.blocked:
                commit('setEmailRequest', { name, count, blocked: true })
                return {
                    success: false,
                    strategy,
                    error: `Cannot download more than ${EMAIL_LIMIT} files in limited resources mode`
                }

            case downloadStrategy.direct:
                return executeDirectDownload({ commit }, { name, count, onDirect })

            case downloadStrategy.email:
                try {
                    const email = await new Promise((resolve, reject) => {
                        commit('setEmailRequest', { name, count, blocked: false, resolve, reject })
                    })
                    await onEmail(email)
                    return { success: true, strategy }
                } catch (error) {
                    return { success: false, strategy, cancelled: true }
                }

            case downloadStrategy.batch:
                return executeBatchDownload({ state, commit }, { name, count, onBatch })

            case downloadStrategy.longtask:
                return executeLongTaskDownload({ commit, dispatch }, { name, count, onLongTask })

            default:
                return { success: false, error: 'Unknown strategy' }
        }
    },

    /**
     * Unified download action backed by POST /dashboard/v3/media/download/.
     * Server decides the delivery mode (direct | archive | email); the client
     * just routes the response.
     *
     * When count exceeds the client-side threshold and no explicit delivery is
     * set, the user is prompted to choose between waiting in-browser or having
     * a download link emailed.
     */
    async downloadMedia({ state, commit, dispatch }, { name, media, options, count }) {
        const effectiveOptions = { ...(options || {}) }
        const deliveryFromConsumer = effectiveOptions.delivery && effectiveOptions.delivery !== 'auto'

        if (!deliveryFromConsumer && count > state.batchSize) {
            const promptResult = await promptDeliveryChoice({ commit }, { name, count })
            if (promptResult.cancelled) {
                return { success: false, cancelled: true }
            }
            Object.assign(effectiveOptions, promptResult.options)
        }

        return await runUnifiedDownload(
            { commit, dispatch },
            { name, media, options: effectiveOptions, count }
        )
    },

    /**
     * Handle unified MQTT events (media_archive.*) for the new endpoint.
     * Reconciles parts as they arrive (resilient to lost MQTT messages).
     */
    handleMediaArchiveMessage({ commit, getters }, { data, sessionId }) {
        const session = getters.sessionById(sessionId)
        if (!session) return

        const persistedEntry = getPersistedSessions().find(s => s.sessionId === session.id)
        const downloadedParts = persistedEntry?.downloadedParts || []

        const availableUrls = extractMediaArchivePartUrls(data)
        downloadMissingParts(availableUrls, downloadedParts)

        const messageState = resolveMediaArchiveState(data)

        if (messageState.isFinal) {
            taskObserver.unobserve(session.taskReference)
            commit('updateSession', {
                id: session.id,
                status: messageState.status,
                percentage: messageState.status === downloadSessionStatus.completed ? 100 : session.percentage,
                zipPartsReceived: downloadedParts.length,
                downloadedFiles: messageState.filesProcessed || session.totalFiles,
                failedDownloads: messageState.failedDownloads || 0,
                error: messageState.error || null
            })
            unpersistSession(session.id)
        } else {
            commit('updateSession', {
                id: session.id,
                status: downloadSessionStatus.inProgress,
                percentage: messageState.percentage ?? session.percentage,
                zipPartsReceived: downloadedParts.length,
                downloadedFiles: messageState.filesProcessed || session.downloadedFiles
            })
            if (persistedEntry) {
                persistedEntry.downloadedParts = downloadedParts
                persistedEntry.percentage = messageState.percentage ?? persistedEntry.percentage
                persistSession(persistedEntry)
            }
        }
    },

    cancelDownload({ commit, getters }, sessionId) {
        const session = getters.sessionById(sessionId)
        if (session) {
            if (session.strategy === downloadStrategy.longtask) {
                if (session.taskReference) {
                    taskObserver.unobserve(session.taskReference)
                }
                unpersistSession(sessionId)
            }
            commit('updateSession', {
                id: sessionId,
                cancelled: true,
                status: downloadSessionStatus.cancelled
            })
        }
    },

    removeSession({ commit, getters }, sessionId) {
        const session = getters.sessionById(sessionId)
        if (session?.strategy === downloadStrategy.longtask) {
            unpersistSession(sessionId)
        }
        commit('removeSession', sessionId)
    },

    clearCompletedSessions({ commit, getters }) {
        getters.sessions
            .filter(s => s.status !== downloadSessionStatus.inProgress &&
                         s.status !== downloadSessionStatus.pending)
            .forEach(s => {
                if (s.strategy === downloadStrategy.longtask) {
                    unpersistSession(s.id)
                }
                commit('removeSession', s.id)
            })
    },

    /**
     * Handles any long task message (MQTT or poll) with unified reconciliation.
     * Every message triggers a check for undownloaded parts, making the system
     * resilient to lost MQTT messages.
     */
    handleLongTaskMessage({ commit, getters }, { data, source, sessionId }) {
        const session = getters.sessionById(sessionId)
        if (!session) return

        const persistedEntry = getPersistedSessions().find(s => s.sessionId === session.id)
        const downloadedParts = persistedEntry?.downloadedParts || []

        // Always reconcile: download any parts we haven't downloaded yet
        const availableUrls = extractZipPartUrls(data, source)
        downloadMissingParts(availableUrls, downloadedParts)

        const messageState = resolveMessageState(data, source)

        if (messageState.isFinal) {
            taskObserver.unobserve(session.taskReference)
            commit('updateSession', {
                id: session.id,
                status: messageState.status,
                percentage: messageState.status === downloadSessionStatus.completed ? 100 : session.percentage,
                zipPartsReceived: downloadedParts.length,
                downloadedFiles: messageState.filesProcessed || session.totalFiles,
                failedDownloads: messageState.failedDownloads || 0,
                error: messageState.error || null
            })
            unpersistSession(session.id)
        } else {
            commit('updateSession', {
                id: session.id,
                status: downloadSessionStatus.inProgress,
                percentage: messageState.percentage ?? session.percentage,
                zipPartsReceived: downloadedParts.length,
                downloadedFiles: messageState.filesProcessed || session.downloadedFiles
            })
            if (persistedEntry) {
                persistedEntry.downloadedParts = downloadedParts
                persistedEntry.percentage = messageState.percentage ?? persistedEntry.percentage
                persistSession(persistedEntry)
            }
        }
    },

    restoreLongTasks({ commit, dispatch }) {
        const persisted = getPersistedSessions()
        for (const entry of persisted) {
            commit('createSession', {
                id: entry.sessionId,
                name: entry.name,
                status: downloadSessionStatus.inProgress,
                strategy: downloadStrategy.longtask,
                totalFiles: entry.totalFiles,
                downloadedFiles: entry.downloadedParts.length,
                taskReference: entry.taskReference,
                percentage: entry.percentage || 0,
                zipPartsReceived: entry.downloadedParts.length,
                failedDownloads: 0,
                cancelled: false,
                error: null,
                createdAt: new Date(entry.createdAt)
            })

            const isMediaArchive = entry.kind === 'media_archive'
            const handler = isMediaArchive ? 'handleMediaArchiveMessage' : 'handleLongTaskMessage'
            taskObserver.observe(entry.taskReference, {
                onMessage: (data, source) => dispatch(handler, {
                    data, source, sessionId: entry.sessionId
                }),
                ...(isMediaArchive ? { pollInterval: 0 } : {})
            })
        }
    }
}

async function promptDeliveryChoice({ commit }, { name, count }) {
    if (!EMAIL_DELIVERY_ENABLED) {
        return { options: { delivery: 'archive' } }
    }

    let choice
    try {
        choice = await new Promise((resolve, reject) => {
            commit('setDeliveryChoiceRequest', { name, count, resolve, reject })
        })
    } catch {
        return { cancelled: true }
    }

    if (choice === 'email') {
        let email
        try {
            email = await new Promise((resolve, reject) => {
                commit('setEmailRequest', { name, count, blocked: false, resolve, reject })
            })
        } catch {
            return { cancelled: true }
        }
        return { options: { delivery: 'email', email } }
    }
    return { options: { delivery: 'archive' } }
}

async function runUnifiedDownload({ commit, dispatch }, { name, media, options, count }) {
    try {
        const response = await mediaDownloadService.download({ media, options })

        if (response.mode === 'direct') {
            return executeUnifiedDirectDownload({ commit }, { name, files: response.files })
        }
        if (response.mode === 'archive') {
            return executeUnifiedArchiveDownload(
                { commit, dispatch },
                { name, count: count || 0, task: response.task }
            )
        }
        if (response.mode === 'email') {
            const message = $gettextInterpolate(
                $gettext("We're preparing your download. A link will be sent to %{email} shortly."),
                { email: options.email }
            )
            dispatch('notifications/success', message, { root: true })
            return { success: true, mode: 'email', message }
        }
        return { success: false, error: `Unknown mode: ${response.mode}` }
    } catch (error) {
        const errPayload = error?.response?.data?.error || error?.data?.error

        // Server-side fallback: count exceeds ARCHIVE_LIMIT, force email
        if (errPayload?.code === 'EMAIL_REQUIRED' && !options.email) {
            let email
            try {
                email = await new Promise((resolve, reject) => {
                    commit('setEmailRequest', { name, count, blocked: false, resolve, reject })
                })
            } catch {
                return { success: false, cancelled: true }
            }
            return await runUnifiedDownload(
                { commit, dispatch },
                { name, media, options: { ...options, delivery: 'email', email }, count }
            )
        }

        return {
            success: false,
            error: errPayload?.message || error?.message || 'Download failed',
            code: errPayload?.code || null,
            details: errPayload?.details || null,
        }
    }
}

/**
 * Extracts media_archive part URLs from MQTT messages.
 * `progress` carries a single `part`; `completed` carries the full `parts` list.
 */
function extractMediaArchivePartUrls(data) {
    const urls = []
    if (data.part?.url) {
        urls.push(data.part.url)
    }
    if (Array.isArray(data.parts)) {
        for (const part of data.parts) {
            const url = typeof part === 'string' ? part : part?.url
            if (url) urls.push(url)
        }
    }
    return urls
}

function resolveMediaArchiveState(data) {
    if (data.type === 'media_archive.completed') {
        return {
            isFinal: true,
            status: downloadSessionStatus.completed,
            filesProcessed: data.processed,
            failedDownloads: data.failed || 0
        }
    }
    if (data.type === 'media_archive.failed') {
        return {
            isFinal: true,
            status: downloadSessionStatus.failed,
            error: data.error || null
        }
    }
    return {
        isFinal: false,
        percentage: data.percentage || 0,
        filesProcessed: data.processed
    }
}

function executeUnifiedDirectDownload({ commit }, { name, files }) {
    const sessionId = uuidv4()
    const count = files.length

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.inProgress,
        totalFiles: count,
        downloadedFiles: 0,
        currentBatch: 0,
        totalBatches: 1,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    Downloader.multiDownload(files)
        .then(() => {
            commit('removeSession', sessionId)
        })
        .catch((error) => {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.failed,
                error: getUserFriendlyError(error)
            })
        })

    return { success: true, mode: 'direct' }
}

function executeUnifiedArchiveDownload({ commit, dispatch }, { name, count, task }) {
    const sessionId = uuidv4()
    const taskReference = task.reference

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.inProgress,
        strategy: downloadStrategy.longtask,
        totalFiles: count,
        downloadedFiles: 0,
        taskReference,
        percentage: 0,
        zipPartsReceived: 0,
        failedDownloads: 0,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    persistSession({
        sessionId,
        name: name || 'Download',
        taskReference,
        totalFiles: count,
        downloadedParts: [],
        percentage: 0,
        kind: 'media_archive',
        createdAt: new Date().toISOString()
    })

    taskObserver.observe(taskReference, {
        onMessage: (data) => dispatch('handleMediaArchiveMessage', { data, sessionId }),
        pollInterval: 0
    })

    return { success: true, mode: 'archive', taskReference }
}

/**
 * Execute direct download with session UI (no pagination, but shows progress)
 * Runs in background - returns immediately after creating session
 */
function executeDirectDownload({ commit }, { name, count, onDirect }) {
    const sessionId = uuidv4()

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.inProgress,
        totalFiles: count,
        downloadedFiles: 0,
        currentBatch: 0,
        totalBatches: 1,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    // Execute in background with retry for 502 errors
    withRetry(() => onDirect())
        .then(() => {
            commit('removeSession', sessionId)
        })
        .catch((error) => {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.failed,
                error: getUserFriendlyError(error)
            })
        })

    // Return immediately
    return { success: true, strategy: downloadStrategy.direct }
}

/**
 * Execute batch download with progress UI
 * Runs in background - returns immediately after creating session
 */
function executeBatchDownload({ state, commit }, { name, count, onBatch }) {
    const sessionId = uuidv4()
    const batchSize = state.batchSize
    const totalBatches = Math.ceil(count / batchSize)

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.inProgress,
        totalFiles: count,
        downloadedFiles: 0,
        currentBatch: 0,
        totalBatches,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    // Execute batch loop in background
    const runBatches = async () => {
        try {
            for (let page = 1; page <= totalBatches; page++) {
                // Check cancellation
                const session = state.sessions.find(s => s.id === sessionId)
                if (session?.cancelled) {
                    return
                }

                commit('updateSession', {
                    id: sessionId,
                    currentBatch: page
                })

                await withRetry(() => onBatch(page, batchSize))

                // Calculate downloaded files based on completed batches
                const isLastBatch = page === totalBatches
                const filesInThisBatch = isLastBatch ? (count - (page - 1) * batchSize) : batchSize
                const downloadedTotal = (page - 1) * batchSize + filesInThisBatch

                commit('updateSession', {
                    id: sessionId,
                    downloadedFiles: downloadedTotal
                })
            }

            // Remove session when completed successfully
            commit('removeSession', sessionId)

        } catch (error) {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.failed,
                error: getUserFriendlyError(error)
            })
        }
    }

    // Start batch loop without awaiting
    runBatches()

    // Return immediately
    return { success: true, strategy: downloadStrategy.batch }
}

/**
 * Execute longtask download: POST to backend, receive task_reference, then MQTT + polling handles progress
 * Runs in background - returns immediately after creating session
 */
function executeLongTaskDownload({ commit, dispatch }, { name, count, onLongTask }) {
    const sessionId = uuidv4()

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.pending,
        strategy: downloadStrategy.longtask,
        totalFiles: count,
        downloadedFiles: 0,
        taskReference: null,
        percentage: 0,
        zipPartsReceived: 0,
        failedDownloads: 0,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    // Execute in background
    onLongTask()
        .then(({ task_reference }) => {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.inProgress,
                taskReference: task_reference
            })

            // Persist session to localStorage for recovery after refresh
            persistSession({
                sessionId,
                name: name || 'Download',
                taskReference: task_reference,
                totalFiles: count,
                downloadedParts: [],
                percentage: 0,
                createdAt: new Date().toISOString()
            })

            // Subscribe to MQTT + start polling
            taskObserver.observe(task_reference, {
                onMessage: (data, source) => dispatch('handleLongTaskMessage', {
                    data, source, sessionId
                })
            })
        })
        .catch((error) => {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.failed,
                error: getUserFriendlyError(error)
            })
        })

    return { success: true, strategy: downloadStrategy.longtask }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
